import { useState } from "react";
import { createPortal } from "react-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Scale, Trophy, Check } from "lucide-react";
import { CARD_GROUPS } from "@/components/CardPicker";

export default function CardCompare({ open, onClose }) {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [customA, setCustomA] = useState("");
  const [customB, setCustomB] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const run = async () => {
    const cardA = customA.trim() || a;
    const cardB = customB.trim() || b;
    if (!cardA || !cardB) return toast.error("Sélectionnez ou saisissez deux cartes");
    if (cardA === cardB) return toast.error("Choisissez deux cartes différentes");
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/cards/compare", { card_a: cardA, card_b: cardB });
      setResult(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Échec de la comparaison");
    } finally {
      setLoading(false);
    }
  };

  const close = () => {
    setResult(null);
    setA("");
    setB("");
    setCustomA("");
    setCustomB("");
    onClose?.();
  };

  const CardSelect = ({ value, onChange, testid }) => (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full h-11 px-3 rounded-xl border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40"
    >
      <option value="">Choisir une carte…</option>
      {CARD_GROUPS.map((g) => (
        <optgroup key={g.network} label={g.network}>
          {g.cards.map((c) => <option key={c} value={c}>{c}</option>)}
        </optgroup>
      ))}
    </select>
  );

  return createPortal(
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-foreground/40 backdrop-blur-sm" onClick={loading ? undefined : close} data-testid="card-compare-modal">
      <div className="w-full max-w-2xl max-h-[88vh] flex flex-col rounded-2xl border border-border bg-card shadow-xl animate-in-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary grid place-items-center"><Scale className="w-4 h-4" /></div>
            <div>
              <p className="font-heading font-bold leading-none">Comparer deux cartes bancaires</p>
              <p className="text-xs text-muted-foreground mt-1">Quelles assurances sont les meilleures ?</p>
            </div>
          </div>
          <button onClick={close} disabled={loading} className="w-8 h-8 grid place-items-center rounded-lg hover:bg-muted transition disabled:opacity-40" data-testid="card-compare-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <CardSelect value={a} onChange={setA} testid="compare-select-a" />
              <input data-testid="compare-custom-a" value={customA} onChange={(e) => setCustomA(e.target.value)} placeholder="ou saisir une autre carte…" className="w-full h-9 px-3 rounded-xl border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40" />
            </div>
            <div className="space-y-2">
              <CardSelect value={b} onChange={setB} testid="compare-select-b" />
              <input data-testid="compare-custom-b" value={customB} onChange={(e) => setCustomB(e.target.value)} placeholder="ou saisir une autre carte…" className="w-full h-9 px-3 rounded-xl border border-input bg-card text-sm outline-none focus:ring-2 focus:ring-ring/40" />
            </div>
          </div>
          <button data-testid="compare-run-btn" onClick={run} disabled={loading} className="w-full h-11 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-50 transition flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Scale className="w-4 h-4" />}
            {loading ? "Comparaison en cours…" : "Comparer"}
          </button>

          {result && (
            <div data-testid="compare-result" className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                {(result.cards || []).map((c, idx) => {
                  const isWinner = (result.winner === "A" && idx === 0) || (result.winner === "B" && idx === 1);
                  return (
                    <div key={idx} data-testid={`compare-card-${idx}`} className={`rounded-xl border p-4 ${isWinner ? "border-emerald-500/50 bg-emerald-500/5" : "border-border"}`}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-heading font-bold text-sm">{c.card}</p>
                        {isWinner && <span className="text-[10px] font-bold uppercase text-emerald-600 flex items-center gap-1 shrink-0"><Trophy className="w-3.5 h-3.5" />Recommandée</span>}
                      </div>
                      <p className="text-xs text-muted-foreground">{c.insurer}</p>
                      <div className="mt-3 space-y-2">
                        {(c.coverages || []).map((g, i) => (
                          <div key={i} className="text-xs">
                            <p className="font-medium flex items-start gap-1.5"><Check className="w-3 h-3 text-emerald-600 mt-0.5 shrink-0" />{g.label}</p>
                            <p className="text-muted-foreground ml-4">{g.limit || "—"}{g.notes ? ` · ${g.notes}` : ""}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              {result.verdict && (
                <div className="rounded-xl bg-accent border border-ochre/25 p-4">
                  <p className="text-xs font-mono uppercase tracking-wide text-ochre mb-1">Verdict</p>
                  <p className="text-sm text-accent-foreground">{result.verdict}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
